(() => {
  'use strict';

  const SETTINGS = {
    /* Zoom activates only above this screen width */
    minimumDesktopWidth: 990,

    /* Zoom strength */
    zoomMultiplier: 2.5,

    /* Gap between original image and zoom panel */
    gap: 18,

    /* Maximum zoom-result dimensions */
    resultWidth: 600,
    resultHeight: 650,

    /* Minimum preview width required */
    minimumResultWidth: 320,

    /*
      Use "right" to match Amazon.
      Change to "left" if you want the zoom panel on the left.
    */
    resultSide: 'right'
  };

  const SELECTORS = {
    gallery: [
      'product-media-gallery',
      '.product__media-gallery',
      '.product-gallery',
      '[id^="MediaGallery-"]'
    ].join(','),

    media: [
      '.product__media-item',
      '.product__media',
      '[data-media-id]'
    ].join(','),

    image: [
      '.product__media img',
      '.product__media-item img',
      'product-media-gallery img'
    ].join(',')
  };

  let lens = null;
  let result = null;

  let activeMedia = null;
  let activeImage = null;

  let currentResultWidth = 0;
  let currentResultHeight = 0;

  let animationFrame = null;

  function isDesktop() {
    return window.innerWidth >= SETTINGS.minimumDesktopWidth;
  }

  function createZoomElements() {
    if (!lens) {
      lens = document.createElement('div');
      lens.className = 'amazon-zoom-lens';
      lens.setAttribute('aria-hidden', 'true');
    }

    if (!result) {
      result = document.createElement('div');
      result.className = 'amazon-zoom-result';
      result.setAttribute('aria-hidden', 'true');
      document.body.appendChild(result);
    }
  }

  function getImageSource(image) {
    return (
      image.currentSrc ||
      image.getAttribute('src') ||
      image.dataset.src ||
      ''
    );
  }

  function isUsableImage(image) {
    if (!image) return false;
    if (!image.complete) return false;
    if (!image.naturalWidth || !image.naturalHeight) return false;

    const rect = image.getBoundingClientRect();

    if (rect.width < 100 || rect.height < 100) return false;

    /*
      Ignore hidden slider images.
    */
    if (
      rect.bottom <= 0 ||
      rect.top >= window.innerHeight ||
      rect.right <= 0 ||
      rect.left >= window.innerWidth
    ) {
      return false;
    }

    return true;
  }

  function getMediaContainer(image) {
    return (
      image.closest('.product__media') ||
      image.closest('.product__media-item') ||
      image.closest('[data-media-id]') ||
      image.parentElement
    );
  }

  function calculateResultBox(imageRect) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const pageMargin = 15;
    const gap = SETTINGS.gap;

    let side = SETTINGS.resultSide;
    let availableWidth;

    if (side === 'left') {
      availableWidth = imageRect.left - gap - pageMargin;

      if (availableWidth < SETTINGS.minimumResultWidth) {
        side = 'right';
      }
    }

    if (side === 'right') {
      availableWidth =
        viewportWidth - imageRect.right - gap - pageMargin;

      if (availableWidth < SETTINGS.minimumResultWidth) {
        const leftAvailable =
          imageRect.left - gap - pageMargin;

        if (leftAvailable >= SETTINGS.minimumResultWidth) {
          side = 'left';
          availableWidth = leftAvailable;
        }
      }
    }

    const resultWidth = Math.min(
      SETTINGS.resultWidth,
      availableWidth
    );

    const availableHeight =
      viewportHeight - pageMargin * 2;

    const resultHeight = Math.min(
      SETTINGS.resultHeight,
      availableHeight,
      Math.max(imageRect.height, 400)
    );

    let left;

    if (side === 'left') {
      left = imageRect.left - resultWidth - gap;
    } else {
      left = imageRect.right + gap;
    }

    let top = imageRect.top;

    if (top < pageMargin) {
      top = pageMargin;
    }

    if (top + resultHeight > viewportHeight - pageMargin) {
      top = viewportHeight - resultHeight - pageMargin;
    }

    return {
      side,
      left,
      top,
      width: resultWidth,
      height: resultHeight
    };
  }

  function showZoom(image, media) {
    if (!isDesktop()) return;
    if (!isUsableImage(image)) return;

    createZoomElements();
    hideZoom();

    activeImage = image;
    activeMedia = media;

    const imageRect = image.getBoundingClientRect();
    const resultBox = calculateResultBox(imageRect);

    if (resultBox.width < SETTINGS.minimumResultWidth) {
      return;
    }

    activeMedia.classList.add('amazon-zoom-source');
    activeMedia.appendChild(lens);

    currentResultWidth = resultBox.width;
    currentResultHeight = resultBox.height;

    result.style.left = `${resultBox.left}px`;
    result.style.top = `${resultBox.top}px`;
    result.style.width = `${resultBox.width}px`;
    result.style.height = `${resultBox.height}px`;

    const imageSource = getImageSource(image);

    result.style.backgroundImage = `url("${imageSource}")`;
    result.style.backgroundSize = `${
      imageRect.width * SETTINGS.zoomMultiplier
    }px ${
      imageRect.height * SETTINGS.zoomMultiplier
    }px`;

    const lensWidth =
      resultBox.width / SETTINGS.zoomMultiplier;

    const lensHeight =
      resultBox.height / SETTINGS.zoomMultiplier;

    lens.style.width = `${Math.min(
      lensWidth,
      imageRect.width
    )}px`;

    lens.style.height = `${Math.min(
      lensHeight,
      imageRect.height
    )}px`;

    lens.style.display = 'block';
    result.style.display = 'block';
  }

  function hideZoom() {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }

    if (lens) {
      lens.style.display = 'none';

      if (lens.parentNode) {
        lens.parentNode.removeChild(lens);
      }
    }

    if (result) {
      result.style.display = 'none';
      result.style.backgroundImage = '';
      result.style.backgroundPosition = '';
      result.style.backgroundSize = '';
    }

    if (activeMedia) {
      activeMedia.classList.remove('amazon-zoom-source');
    }

    activeMedia = null;
    activeImage = null;
  }

  function moveZoom(event) {
    if (!activeImage || !activeMedia || !lens || !result) {
      return;
    }

    if (!isDesktop()) {
      hideZoom();
      return;
    }

    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }

    animationFrame = requestAnimationFrame(() => {
      const imageRect = activeImage.getBoundingClientRect();
      const mediaRect = activeMedia.getBoundingClientRect();

      const lensWidth = lens.offsetWidth;
      const lensHeight = lens.offsetHeight;

      /*
        Mouse location inside the image.
      */
      let pointerX = event.clientX - imageRect.left;
      let pointerY = event.clientY - imageRect.top;

      /*
        Keep the lens completely inside the image.
      */
      let lensX = pointerX - lensWidth / 2;
      let lensY = pointerY - lensHeight / 2;

      const maximumLensX = imageRect.width - lensWidth;
      const maximumLensY = imageRect.height - lensHeight;

      lensX = Math.max(
        0,
        Math.min(lensX, maximumLensX)
      );

      lensY = Math.max(
        0,
        Math.min(lensY, maximumLensY)
      );

      /*
        Position relative to the media container.
      */
      const relativeLeft =
        imageRect.left - mediaRect.left + lensX;

      const relativeTop =
        imageRect.top - mediaRect.top + lensY;

      lens.style.left = `${relativeLeft}px`;
      lens.style.top = `${relativeTop}px`;

      /*
        Move zoomed image in opposite direction.
      */
      const backgroundX =
        lensX * SETTINGS.zoomMultiplier;

      const backgroundY =
        lensY * SETTINGS.zoomMultiplier;

      result.style.backgroundPosition =
        `-${backgroundX}px -${backgroundY}px`;
    });
  }

  function handleMouseEnter(event) {
    if (!isDesktop()) return;

    const image = event.target.closest('img');

    if (!image || !image.matches(SELECTORS.image)) {
      return;
    }

    const media = getMediaContainer(image);

    if (!media) return;

    /*
      Do not activate zoom for video/model media.
    */
    if (
      media.querySelector(
        'video, iframe, model-viewer, .deferred-media'
      )
    ) {
      return;
    }

    showZoom(image, media);
    moveZoom(event);
  }

  function handleMouseMove(event) {
    if (!activeImage) return;

    const imageRect = activeImage.getBoundingClientRect();

    const insideImage =
      event.clientX >= imageRect.left &&
      event.clientX <= imageRect.right &&
      event.clientY >= imageRect.top &&
      event.clientY <= imageRect.bottom;

    if (!insideImage) {
      hideZoom();
      return;
    }

    moveZoom(event);
  }

  function handleMouseLeave(event) {
    if (!activeMedia) return;

    const nextElement = event.relatedTarget;

    if (
      nextElement &&
      activeMedia.contains(nextElement)
    ) {
      return;
    }

    hideZoom();
  }

  function bindImage(image) {
    if (image.dataset.amazonZoomBound === 'true') {
      return;
    }

    image.dataset.amazonZoomBound = 'true';

    image.addEventListener(
      'mouseenter',
      handleMouseEnter
    );

    image.addEventListener(
      'mousemove',
      handleMouseMove
    );

    image.addEventListener(
      'mouseleave',
      handleMouseLeave
    );

    image.addEventListener('load', hideZoom);
  }

  function initializeZoom() {
    createZoomElements();

    document
      .querySelectorAll(SELECTORS.image)
      .forEach(bindImage);
  }

  /*
    Dawn can rebuild gallery elements when variants change.
  */
  const observer = new MutationObserver(() => {
    initializeZoom();
  });

  function start() {
    initializeZoom();

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    window.addEventListener(
      'scroll',
      hideZoom,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      hideZoom,
      { passive: true }
    );

    document.addEventListener(
      'shopify:section:load',
      initializeZoom
    );

    document.addEventListener(
      'shopify:section:unload',
      hideZoom
    );

    document.addEventListener(
      'variant:change',
      hideZoom
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start
    );
  } else {
    start();
  }
  
})();